import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { ensureUrl } from "@/lib/format-url"
import type { BreadcrumbItem } from "@/types"

function formatPhone(phone: string | null): string {
  if (!phone) return ""
  const digits = phone.replace(/\D/g, "")
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return phone
}

interface Customer {
  id: number
  display_name: string
}

interface Location {
  id: number
  name: string | null
  display_name: string
  address: string | null
  address2: string | null
  city: string | null
  state: string | null
  zip: string | null
  phone: string | null
  fax: string | null
  email: string | null
  website: string | null
  notes: string | null
  sales_rep_name: string | null
}

interface Contact {
  id: number
  name: string
  title: string | null
  phone: string | null
  email: string | null
  status: number
}

interface Props {
  customer: Customer
  location: Location
  contacts: Contact[]
  can_edit: boolean
}

export default function Show({
  customer,
  location,
  contacts,
  can_edit,
}: Props) {
  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this location?")) {
      router.delete(`/customers/${customer.id}/locations/${location.id}`)
    }
  }

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Customers", href: "/customers" },
    { title: customer.display_name, href: `/customers/${customer.id}` },
    { title: "Locations", href: `/customers/${customer.id}/locations` },
    {
      title: location.display_name || "Location",
      href: `/customers/${customer.id}/locations/${location.id}`,
    },
  ]

  const cityStateZip = [
    location.city,
    location.state
      ? `${location.state} ${location.zip ?? ""}`.trim()
      : location.zip,
  ]
    .filter(Boolean)
    .join(", ")

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`${location.display_name} - ${customer.display_name}`} />

      <div className="p-6">
        <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Left column: Address info */}
          <div>
            <div className="mb-2 flex items-center gap-2">
              {location.name && (
                <h2 className="text-xl font-semibold">{location.name}</h2>
              )}
              {can_edit && (
                <>
                  <Link
                    href={`/customers/${customer.id}/locations/${location.id}/edit`}
                    className="text-hpi-active-blue hover:underline"
                  >
                    <i className="fa fa-edit" />
                  </Link>
                  <button
                    onClick={handleDelete}
                    className="text-hpi-red hover:underline"
                  >
                    <i className="fa fa-trash" />
                  </button>
                </>
              )}
            </div>
            {location.address && <div>{location.address}</div>}
            {location.address2 && <div>{location.address2}</div>}
            {cityStateZip && <div>{cityStateZip}</div>}
          </div>

          {/* Right column: Contact info & rep */}
          <div>
            {location.sales_rep_name && (
              <div>
                <span className="font-semibold">Rep:</span>{" "}
                {location.sales_rep_name}
              </div>
            )}
            {location.phone && (
              <div>
                <span className="font-semibold">Phone:</span>{" "}
                {formatPhone(location.phone)}
              </div>
            )}
            {location.fax && (
              <div>
                <span className="font-semibold">Fax:</span>{" "}
                {formatPhone(location.fax)}
              </div>
            )}
            {location.email && (
              <div>
                <span className="font-semibold">Email:</span> {location.email}
              </div>
            )}
            {location.website && (
              <div>
                <span className="font-semibold">Website:</span>{" "}
                <a
                  href={ensureUrl(location.website)!}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-hpi-active-blue hover:underline"
                >
                  {location.website}
                </a>
              </div>
            )}
          </div>
        </div>

        {location.notes && (
          <div className="mb-6">
            <span className="font-semibold">Notes:</span> {location.notes}
          </div>
        )}

        {/* Contacts at this location */}
        {contacts.length > 0 && (
          <div className="mt-6">
            <h3 className="mb-3 text-lg font-semibold">
              Contacts at this Location
            </h3>
            <table className="border-hpi-light-gray w-full border-collapse border">
              <thead>
                <tr className="bg-hpi-pale-gray">
                  <th className="border-hpi-light-gray border px-4 py-2 text-left">
                    Name
                  </th>
                  <th className="border-hpi-light-gray hidden border px-4 py-2 text-left md:table-cell">
                    Title
                  </th>
                  <th className="border-hpi-light-gray hidden border px-4 py-2 text-left md:table-cell">
                    Phone
                  </th>
                  <th className="border-hpi-light-gray hidden border px-4 py-2 text-left md:table-cell">
                    Email
                  </th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => (
                  <tr
                    key={contact.id}
                    className={`hover:bg-hpi-pale-blue ${contact.status === 50 ? "opacity-50" : ""}`}
                  >
                    <td className="border-hpi-light-gray border px-4 py-2">
                      <Link
                        href={`/customers/${customer.id}/contacts/${contact.id}/edit`}
                        className="text-hpi-active-blue hover:underline"
                      >
                        {contact.name}
                        {contact.status === 90 && (
                          <i className="fa fa-ban text-hpi-red ml-1" />
                        )}
                      </Link>
                    </td>
                    <td className="border-hpi-light-gray hidden border px-4 py-2 md:table-cell">
                      {contact.title}
                    </td>
                    <td className="border-hpi-light-gray hidden border px-4 py-2 md:table-cell">
                      {contact.phone}
                    </td>
                    <td className="border-hpi-light-gray hidden border px-4 py-2 md:table-cell">
                      {contact.email}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4">
          <Link
            href={`/customers/${customer.id}/locations`}
            className="text-hpi-active-blue hover:underline"
          >
            Return to Location List
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
